import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonService } from '@app/core';
import { SideNavBarLinksData } from '@app/shared/data/values';

@Component({
    selector: 'app-default-redirect',
    template: '',
    standalone: false
})
export class DefaultRedirectComponent implements OnInit {
  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly commonService: CommonService
  ) {}

  ngOnInit(): void {
    const parentPath = this.activatedRoute.snapshot.data['parentPath'];
    const defaultChildPath = this.activatedRoute.snapshot.data['defaultChildPath'];

    // Get all child routes for the parentPath from SideNavBarLinksData
    const childRoutes = this.getChildRoutes(parentPath);

    // Check if the defaultChildPath is accessible
    if (defaultChildPath && this.isRouteAccessible(defaultChildPath, childRoutes)) {
      this.router.navigateByUrl(defaultChildPath).catch((error) => {
        console.error('Navigation failed to default child route:', error);
        this.navigateToFallback(childRoutes);
      });
    } else {
      this.navigateToFallback(childRoutes);
    }
  }

  /**
   * Retrieves child routes for the given parentPath from SideNavBarLinksData.
   */
  private getChildRoutes(parentPath: string): any[] {
    const parentNav = SideNavBarLinksData.navs.find(nav => nav.route.includes(parentPath));
    return parentNav?.subRoutes || [];
  }

  /**
   * Determines if a route is accessible based on its configuration.
   */
  private isRouteAccessible(routePath: string, childRoutes: any[]): boolean {
    const userFeatures = new Map(
      this.commonService.appProfileDataAsValue?.user.features.map(feature => [
        feature.featureName,
        feature.access_level,
      ])
    );

    const matchingRoute = childRoutes.find(route => route.route.join('/') === routePath);

    if (matchingRoute) {
      const featureName = matchingRoute.featureName;
      return userFeatures.has(featureName) && userFeatures.get(featureName) !== 'no_access';
    }

    return false;
  }

  /**
   * Navigates to the first accessible child route or falls back to the dashboard.
   */
  private navigateToFallback(childRoutes: any[]): void {
    const accessibleRoute = this.findAccessibleRoute(childRoutes);

    if (accessibleRoute) {
      this.router.navigateByUrl(accessibleRoute).catch((error) => {
        console.error('Navigation failed to fallback route:', error);
        this.router.navigate(['/home/dashboard'], { replaceUrl: true });
      });
    } else {
      this.router.navigate(['/home/dashboard'], { replaceUrl: true });
    }
  }

  /**
   * Finds the first accessible child route dynamically.
   */
  private findAccessibleRoute(childRoutes: any[]): string | null {
    const userFeatures = new Map(
      this.commonService.appProfileDataAsValue?.user.features.map(feature => [
        feature.featureName,
        feature.access_level,
      ])
    );

    for (const childRoute of childRoutes) {
      const featureName = childRoute.featureName;
      if (userFeatures.has(featureName) && userFeatures.get(featureName) !== 'no_access') {
        return childRoute.route.join('/');
      }
    }

    return null; // No accessible route found
  }
}